import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, MobileCard, MobileCardRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Building2, Key, Link, MoreHorizontal, Edit, Trash, Users, Info, Plus, FileUp, User } from "lucide-react";
import { getInitials } from "@/lib/utils";

interface Team {
  id: number;
  name: string;
  description?: string;
  licenseCount: number;
  memberCount?: number;
}

interface TeamsTableProps {
  teams: Team[];
  filteredTeams: Team[];
  paginatedTeams: Team[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onViewDetails: (team: Team) => void;
  onViewLicenses: (team: Team) => void;
  onEdit: (team: Team) => void;
  onManageMembers: (team: Team) => void;
  onDelete: (id: number, name: string) => void;
  onAssignLicense: (id: number) => void;
  onCreateTeam: () => void;
}

export function TeamsTableWithPagination({
  teams,
  filteredTeams,
  paginatedTeams,
  isLoading,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  onViewLicenses,
  onEdit,
  onManageMembers,
  onDelete,
  onAssignLicense,
  onCreateTeam
}: TeamsTableProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (filteredTeams.length === 0) {
    return (
      <EmptyState
        icon={<Building2 className="h-10 w-10 text-slate-400" />}
        title="No teams found"
        description={teams.length === 0 ? "Add teams to get started" : "Try adjusting your search"}
        action={
          <Button onClick={onCreateTeam} variant="default">
            <Plus className="h-4 w-4 mr-2" />
            Add Team
          </Button>
        }
      />
    );
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="rounded-md border">
        <Table className="hidden md:table" responsive={false}>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="min-w-[200px]">Team</TableHead>
              <TableHead className="hidden sm:table-cell min-w-[150px]">Description</TableHead>
              <TableHead className="hidden sm:table-cell min-w-[100px]">Members</TableHead>
              <TableHead className="min-w-[100px]">Licenses</TableHead>
              <TableHead className="text-right min-w-[140px] sticky right-0 bg-slate-50 dark:bg-slate-900 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedTeams.map((team) => (
              <TableRow key={team.id}>
                <TableCell className="min-w-[200px]" truncate>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className="bg-slate-200 text-slate-700 text-xs">
                        {getInitials(team.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <button 
                        className="font-medium text-left hover:text-primary hover:underline focus:outline-none truncate block"
                        onClick={() => onViewDetails(team)}
                        data-testid={`button-view-team-${team.id}`}
                      >
                        {team.name}
                      </button>
                      <div className="text-xs text-slate-500 truncate">
                        {team.description || "No description"}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell" truncate>
                  {team.description ? (
                    <span className="text-sm truncate block">{team.description}</span>
                  ) : (
                    <span className="text-slate-400 text-sm">No description</span>
                  )}
                </TableCell>
                <TableCell className="hidden sm:table-cell" nowrap>
                  <div className="flex items-center">
                    <Users className="h-3.5 w-3.5 text-slate-600 mr-1.5" />
                    <span className="text-sm">
                      {team.memberCount || 0} members
                    </span>
                  </div>
                </TableCell>
                <TableCell nowrap>
                  {team.licenseCount > 0 ? (
                    <div className="flex items-center">
                      <Key className="h-3.5 w-3.5 text-primary mr-1.5" />
                      <span className="text-sm">{team.licenseCount} licenses</span>
                    </div>
                  ) : (
                    <span className="text-slate-400 text-sm">No licenses</span>
                  )}
                </TableCell>
                <TableCell className="text-right sticky right-0 bg-white dark:bg-slate-900 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]">
                  <div className="flex justify-end items-center gap-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => onAssignLicense(team.id)}
                            data-testid={`button-assign-license-${team.id}`}
                          >
                            <Link className="h-4 w-4" />
                            <span className="sr-only">Assign License</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Assign License</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => onViewDetails(team)}>
                          <Info className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onViewLicenses(team)}>
                          <Key className="h-4 w-4 mr-2" />
                          View Licenses
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEdit(team)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Team
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onManageMembers(team)}>
                          <User className="h-4 w-4 mr-2" />
                          Manage Members
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => onDelete(team.id, team.name)}
                        >
                          <Trash className="h-4 w-4 mr-2" />
                          Delete Team
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {/* Mobile Cards */}
        <div className="md:hidden space-y-4 p-4">
          {paginatedTeams.map((team) => (
            <MobileCard key={team.id}>
              <div className="flex items-center space-x-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-slate-200 text-slate-700 text-xs">
                    {getInitials(team.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <button 
                    className="font-medium text-left hover:text-primary hover:underline focus:outline-none truncate block"
                    onClick={() => onViewDetails(team)}
                    data-testid={`button-view-team-mobile-${team.id}`}
                  >
                    {team.name}
                  </button>
                </div>
              </div>
              
              <MobileCardRow label="Description">
                {team.description || "No description"}
              </MobileCardRow>
              
              <MobileCardRow label="Members">
                <div className="flex items-center">
                  <Users className="h-3.5 w-3.5 text-slate-600 mr-1.5" />
                  {team.memberCount || 0} members
                </div>
              </MobileCardRow>
              
              <MobileCardRow label="Licenses">
                {team.licenseCount > 0 ? (
                  <div className="flex items-center">
                    <Key className="h-3.5 w-3.5 text-primary mr-1.5" />
                    {team.licenseCount} licenses
                  </div>
                ) : (
                  <span className="text-slate-400">No licenses</span>
                )}
              </MobileCardRow>
              
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAssignLicense(team.id)}
                  data-testid={`button-assign-license-mobile-${team.id}`}
                >
                  <Link className="h-4 w-4 mr-2" />
                  Assign
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onEdit(team)}
                  data-testid={`button-edit-team-mobile-${team.id}`}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </MobileCard>
          ))}
        </div>
        
        {/* Pagination component */}
        {filteredTeams.length > 0 && (
          <div className="border-t px-4 bg-slate-50">
            <DataTablePagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredTeams.length}
              onPageChange={onPageChange}
              onPageSizeChange={(size) => {
                onPageSizeChange(size);
                onPageChange(1); // Reset to first page when changing page size
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}